import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Spinner from '../components/common/Spinner';
import { generateAlzheimerReportHTML } from '../services/alzheimerReportService';
import { requestPDF } from '../services/pdfService';
import { generatePDFFilename } from '../utils/generatePDFFilename';
import { patientService } from '../services/patientService';

const AlzheimerReportPreviewPage = () => {
    const { patientId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [htmlContent, setHtmlContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [patientName, setPatientName] = useState(location.state?.patientName || '');
    const [orderId, setOrderId] = useState(location.state?.orderId || '');

    useEffect(() => {
        const createPreview = async () => {
            try {
                let reportData = null;
                
                const storedData = sessionStorage.getItem('alzheimerReportData');
                if (storedData) {
                    reportData = JSON.parse(storedData);
                    sessionStorage.removeItem('alzheimerReportData');
                } else {
                    reportData = location.state?.reportData;
                }

                if (!reportData) {
                    throw new Error('No report data provided');
                }

                // Fetch patient data separately for filename (don't modify reportData)
                const { data: patientResp } = await patientService.getPatientById(patientId);
                const patient = patientResp?.data;

                const fetchedPatientName = patient?.profile?.name;
                const fetchedOrderId = patient?.sukra_order_id || patient?.id;

                // Store patient info for download
                setPatientName(fetchedPatientName || '');
                setOrderId(fetchedOrderId || '');

                console.log('📄 Preview Page - Patient Info:');
                console.log('Patient Name:', fetchedPatientName);
                console.log('Order ID:', fetchedOrderId);

                const html = await generateAlzheimerReportHTML(reportData);
                setHtmlContent(html);
            } catch (error) {
                console.error('Failed to generate preview', error);
                setHtmlContent('<p class="text-red-600">Failed to generate preview</p>');
            } finally {
                setLoading(false);
            }
        };

        createPreview();
    }, [patientId, location.state]);

    const handleDownload = async () => {
        if (!htmlContent) return;
        try {
            setDownloading(true);
            const blob = await requestPDF(htmlContent);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const finalPatientName = patientName || 'patient';
            const finalOrderId = orderId || patientId;
            a.download = generatePDFFilename(finalPatientName, 'alzheimer', finalOrderId);
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('PDF generation failed', err);
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <Layout showSidebar={false}>
                <div className="flex justify-center items-center h-full py-12">
                    <Spinner size="lg" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout showSidebar={false}>
            <div className="flex justify-end mb-4 gap-4">
                <button
                    onClick={handleDownload}
                    disabled={downloading || !htmlContent}
                    className="px-4 py-2 bg-primary-500 text-white rounded disabled:opacity-50"
                >
                    {downloading ? 'Generating…' : 'Download PDF'}
                </button>
            </div>
            <iframe
                title="Alzheimer Report Preview"
                srcDoc={htmlContent}
                className="w-full min-h-screen border-none"
                sandbox="allow-same-origin allow-scripts allow-popups"
            />
        </Layout>
    );
};

export default AlzheimerReportPreviewPage;
